import React, { useEffect, useState } from "react";
import { Tag } from "antd";
import { formatDate } from "../../../../utils/dateConversion";
import Table from "../../../../components/atoms/Tables/Table";
import TableLoading from "../../../../components/Loading/TableLoading";
import Dashboard from "../../../../api/Dashboard";
import PaginationView from "../../../../components/molecules/Pagination";

const PAGE_SIZE = 15;

const ApplicationsView = () => {
  const [applicationsData, setApplicationsData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [statsData, setStatsData] = useState({
    last24HoursCount: 0,
    last3DaysCount: 0,
    last7DaysCount: 0,
    last30DaysCount: 0,
    last14DaysCount: 0,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: PAGE_SIZE,
    total: 0,
  });

  // Fetch count only on initial load
  useEffect(() => {
    const fetchApplicationsCount = async () => {
      try {
        const response = await Dashboard.getJobAnalyticsCount("applications");
        const counts = response?.data?.data || {};

        const updatedStatsData = {
          last24HoursCount: counts["24HoursCount"] || 0,
          last3DaysCount: counts["3DaysCount"] || 0,
          last7DaysCount: counts["7DaysCount"] || 0,
          last30DaysCount: counts["30DaysCount"] || 0,
          last14DaysCount: counts["14DaysCount"] || 0,
        };

        setStatsData(updatedStatsData);
        setPagination((prev) => ({
          ...prev,
          total: counts["14DaysCount"] || 0,
        }));

        // Fetch initial applications data
        fetchApplicationsData(1, PAGE_SIZE);
      } catch (error) {
        console.error("Error fetching applications count:", error);
      }
    };

    fetchApplicationsCount();
  }, []);

  // Fetch applications data on pagination change
  useEffect(() => {
    fetchApplicationsData(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize]);

  const fetchApplicationsData = async (page = 1, limit = PAGE_SIZE) => {
    setIsLoading(true);
    try {
      const response = await Dashboard.getRecentSeekerApplications(page, limit);
      const allApplications = response.data.data;

      // Filter applications for the last 14 days
      const now = new Date();
      const last14Days = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
      const filteredApplications = allApplications.filter(
        (app) => new Date(app.created_at) >= last14Days
      );

      const transformedApplications = filteredApplications.map((app) => ({
        id: app.id,
        applicant_name: `${app.seeker.first_name} ${app.seeker.last_name}`,
        applicant_email: app.seeker.email,
        applicant_phone: app.seeker.phone,
        city: app.seeker.city || "N/A",
        state: app.seeker.state || "N/A",
        business_name: app.location.name,
        position_title: app.position.title,
        availability: app.interest
          ? JSON.parse(app.interest).join(", ")
          : "N/A",
        status: app.status,
        created_at: app.created_at,
        resume_url:
          app.seeker.file && app.seeker.file.length > 0
            ? app.seeker.file[0].resume_url
            : null,
        skills: app.seeker.skill ? app.seeker.skill.split(",") : [],
        position_interests: app.seeker.position_interests
          ? JSON.parse(app.seeker.position_interests)
          : [],
      }));

      setApplicationsData(transformedApplications);
    } catch (error) {
      console.error("Error fetching applications:", error);
      // Reset to first page if an error occurs
      setPagination((prev) => ({
        ...prev,
        current: 1,
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaginationChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize,
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "applied":
        return "blue";
      case "viewed":
        return "purple";
      case "hired":
        return "green";
      case "cancelled":
        return "red";
      default:
        return "gray";
    }
  };

  return (
    <>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 pt-4">
        <div className="overflow-hidden rounded-lg bg-white border border-warmgray-200 px-4 py-5 shadow sm:p-6">
          <dt className="truncate text-sm font-medium text-gray-500">
            Last 24 Hours
          </dt>
          <dd className="mt-1 text-3xl font-semibold tracking-tight text-indigo-600">
            {statsData.last24HoursCount} Applications
          </dd>
        </div>
        <div className="overflow-hidden rounded-lg bg-white border border-warmgray-200 px-4 py-5 shadow sm:p-6">
          <dt className="truncate text-sm font-medium text-gray-500">
            Last 3 Days
          </dt>
          <dd className="mt-1 text-3xl font-semibold tracking-tight text-blue-600">
            {statsData.last3DaysCount} Applications
          </dd>
        </div>
        <div className="overflow-hidden rounded-lg bg-white border border-warmgray-200 px-4 py-5 shadow sm:p-6">
          <dt className="truncate text-sm font-medium text-gray-500">
            Last 7 Days
          </dt>
          <dd className="mt-1 text-3xl font-semibold tracking-tight text-teal-600">
            {statsData.last7DaysCount} Applications
          </dd>
        </div>
        <div className="overflow-hidden rounded-lg bg-white border border-warmgray-200 px-4 py-5 shadow sm:p-6">
          <dt className="truncate text-sm font-medium text-gray-500">
            Last 30 Days
          </dt>
          <dd className="mt-1 text-3xl font-semibold tracking-tight text-emerald-600">
            {statsData.last30DaysCount} Applications
          </dd>
        </div>
      </div>

      <p className="my-6 font-bold text-lg text-gray-500">
        Applications from last 14 days
      </p>

      <div>
        {isLoading ? (
          <TableLoading Columns={6} />
        ) : (
          <>
            <Table
              headers={
                applicationsData?.length > 0
                  ? [
                      "Job Seeker",
                      "Location",
                      "Position",
                      "Availability",
                      "Status",
                      "Applied On",
                    ]
                  : ["No applications found in the last 14 days"]
              }
              data={applicationsData?.map((application, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap py-3 pr-3 text-sm">
                    <div className="font-medium text-gray-900">
                      {application.applicant_name}
                    </div>
                    <div className="text-gray-500">
                      {application.applicant_email}
                    </div>
                    <div className="text-gray-500">
                      {application.applicant_phone}
                    </div>
                  </td>
                  <td className="whitespace-nowrap py-3 pr-3 text-sm">
                    <div className="text-gray-900">
                      {application.business_name}
                    </div>
                    <div className="text-gray-500">
                      {application.city}, {application.state}
                    </div>
                  </td>
                  <td className="whitespace-nowrap py-3 pr-3 text-sm text-gray-900">
                    {application.position_title}
                  </td>
                  <td className="whitespace-nowrap py-3 pr-3 text-sm text-gray-500">
                    {application?.availability}
                  </td>
                  <td className="whitespace-nowrap py-3 pl-4 pr-3 text-sm">
                    <Tag color={getStatusColor(application.status)}>
                      {application.status.charAt(0).toUpperCase() +
                        application.status.slice(1)}
                    </Tag>
                  </td>
                  <td className="whitespace-nowrap py-3 pr-3 text-sm text-gray-500">
                    {formatDate(application.created_at, "MM-DD-YYYY HH:mm")}
                  </td>
                </tr>
              ))}
            />
            <PaginationView
              current={pagination.current}
              pageSize={pagination.pageSize}
              total={statsData.last14DaysCount}
              onChange={handlePaginationChange}
              className="mt-6 flex justify-end"
            />
          </>
        )}
      </div>
    </>
  );
};

export default ApplicationsView;
